import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "../shared/starfield.css";
import Navbar from "../navbar/Navbar";
import { getPublicModels, addModelToMyModels } from "../../services/aiModels";
import { getAccessToken, hasSession } from "../../services/api.ts";

const isAuthed = () => !!getAccessToken() || hasSession();

// Preview/mock models displayed alongside real API-backed public models on the
// Open Medical Models page. These do not exist as DVAIModel rows in the DB, so
// their "Add to My Models" button is intentionally locked with a note; auth
// users can browse and read the linked publication.
// Publication URLs are the canonical papers where a model was introduced.
const MOCK_MODELS = [
  {
    id: "mock-totalsegmentator",
    is_mock: true,
    name: "TotalSegmentator",
    description: "Robust segmentation of 104 anatomical structures in CT images. Trained on 1204 CT scans with detailed manual annotations; released under Apache-2.0 and widely used as an out-of-the-box whole-body segmentation baseline.",
    entity_type: "model",
    model_type: "segmentation",
    model_type_display: "Segmentation",
    input_types: "CT",
    output_data_type: "Segmentation mask",
    build_status: "ready",
    is_added: false,
    created_at: "2023-07-01",
    created_by_user: { first_name: "Jakob", username: "wasserthal" },
    publication_url: "https://pubs.rsna.org/doi/10.1148/ryai.230024",
    publication_title: "TotalSegmentator: Robust Segmentation of 104 Anatomic Structures in CT Images (Radiology: AI, 2023)",
  },
  {
    id: "mock-sam2",
    is_mock: true,
    name: "SAM 2",
    description: "Segment Anything Model 2 from Meta — promptable image and video segmentation with strong zero-shot generalization. Frequently used as a foundation model for medical imaging annotation workflows.",
    entity_type: "model",
    model_type: "segmentation",
    model_type_display: "Segmentation",
    input_types: "Image / Video (2D)",
    output_data_type: "Segmentation mask",
    build_status: "ready",
    is_added: false,
    created_at: "2024-07-29",
    created_by_user: { first_name: "Meta AI", username: "facebookresearch" },
    publication_url: "https://arxiv.org/abs/2408.00714",
    publication_title: "SAM 2: Segment Anything in Images and Videos (Ravi et al., 2024)",
  },
  {
    id: "mock-nnunet",
    is_mock: true,
    name: "nnU-Net",
    description: "Self-configuring deep-learning framework for medical image segmentation. Automatically adapts preprocessing, network topology, training, and inference to a given dataset; remains a top performer on many public segmentation benchmarks.",
    entity_type: "model",
    model_type: "segmentation",
    model_type_display: "Segmentation",
    input_types: "CT / MRI",
    output_data_type: "Segmentation mask",
    build_status: "ready",
    is_added: false,
    created_at: "2020-12-07",
    created_by_user: { first_name: "Fabian", username: "isensee" },
    publication_url: "https://www.nature.com/articles/s41592-020-01008-z",
    publication_title: "nnU-Net: a self-configuring method for deep learning-based biomedical image segmentation (Nature Methods, 2021)",
  },
  {
    id: "mock-medsam",
    is_mock: true,
    name: "MedSAM",
    description: "Foundation model for universal medical image segmentation, adapted from SAM and fine-tuned on more than one million medical image-mask pairs across CT, MRI, ultrasound, X-ray, endoscopy, and pathology.",
    entity_type: "model",
    model_type: "segmentation",
    model_type_display: "Segmentation",
    input_types: "CT / MRI / US / X-ray",
    output_data_type: "Segmentation mask",
    build_status: "ready",
    is_added: false,
    created_at: "2024-01-22",
    created_by_user: { first_name: "Jun", username: "bowang-lab" },
    publication_url: "https://www.nature.com/articles/s41467-024-44824-z",
    publication_title: "Segment Anything in Medical Images (Nature Communications, 2024)",
  },
  // --- Large language / RAG / QA models ---
  {
    id: "mock-chatgpt",
    is_mock: true,
    name: "ChatGPT (GPT-4)",
    description: "General-purpose large language model from OpenAI. Widely used off-label for medical text summarization, patient-facing explanations, and clinical documentation drafting.",
    entity_type: "model",
    model_type: "llm",
    model_type_display: "Large Language Model",
    input_types: "Free-form text",
    output_data_type: "Generated text",
    icon: "fa-comments",
    color: "#a855f7",
    build_status: "ready",
    is_added: false,
    created_at: "2023-03-14",
    created_by_user: { first_name: "OpenAI", username: "openai" },
    publication_url: "https://arxiv.org/abs/2303.08774",
    publication_title: "GPT-4 Technical Report (OpenAI, 2023)",
  },
  {
    id: "mock-medpalm2",
    is_mock: true,
    name: "Med-PaLM 2",
    description: "Google's medical-domain fine-tune of PaLM 2. Achieves expert-level accuracy on USMLE-style medical licensing questions and produces long-form answers rated close to physician responses.",
    entity_type: "model",
    model_type: "llm",
    model_type_display: "Large Language Model (Medical)",
    input_types: "Clinical question / free text",
    output_data_type: "Long-form answer",
    icon: "fa-comments",
    color: "#a855f7",
    build_status: "ready",
    is_added: false,
    created_at: "2023-05-16",
    created_by_user: { first_name: "Google Research", username: "google-research" },
    publication_url: "https://arxiv.org/abs/2305.09617",
    publication_title: "Towards Expert-Level Medical Question Answering with Large Language Models (Singhal et al., 2023)",
  },
  {
    id: "mock-biogpt",
    is_mock: true,
    name: "BioGPT",
    description: "Domain-specific generative Transformer pre-trained on 15M PubMed abstracts. Used for biomedical text generation, relation extraction, and question answering over the biomedical literature.",
    entity_type: "model",
    model_type: "llm",
    model_type_display: "Large Language Model (Biomedical)",
    input_types: "Biomedical text",
    output_data_type: "Generated text",
    icon: "fa-comments",
    color: "#a855f7",
    build_status: "ready",
    is_added: false,
    created_at: "2022-09-24",
    created_by_user: { first_name: "Microsoft Research", username: "microsoft" },
    publication_url: "https://academic.oup.com/bib/article/23/6/bbac409/6713511",
    publication_title: "BioGPT: generative pre-trained transformer for biomedical text generation and mining (Briefings in Bioinformatics, 2022)",
  },
  {
    id: "mock-openscholar-rag",
    is_mock: true,
    name: "OpenScholar (RAG)",
    description: "Retrieval-augmented language model specialized for scientific literature. Answers open-ended research questions with grounded citations drawn from Semantic Scholar, PubMed, and OpenAlex.",
    entity_type: "model",
    model_type: "rag",
    model_type_display: "Retrieval-Augmented Generation",
    input_types: "Research question",
    output_data_type: "Answer with citations",
    icon: "fa-book",
    color: "#059669",
    build_status: "ready",
    is_added: false,
    created_at: "2024-11-21",
    created_by_user: { first_name: "AI2", username: "allenai" },
    publication_url: "https://arxiv.org/abs/2411.14199",
    publication_title: "OpenScholar: Synthesizing Scientific Literature with Retrieval-Augmented LMs (Asai et al., 2024)",
  },
  // --- Classification / foundation ---
  {
    id: "mock-retfound",
    is_mock: true,
    name: "RETFound",
    description: "Self-supervised foundation model trained on 1.6 million unlabelled retinal images (colour fundus and OCT). Achieves strong performance on downstream tasks such as diabetic retinopathy grading and systemic disease prediction.",
    entity_type: "model",
    model_type: "classification",
    model_type_display: "Foundation Model (Retinal)",
    input_types: "Fundus / OCT",
    output_data_type: "Disease grade",
    icon: "fa-eye",
    color: "#2563eb",
    build_status: "ready",
    is_added: false,
    created_at: "2023-09-13",
    created_by_user: { first_name: "Yukun", username: "rmaphoh" },
    publication_url: "https://www.nature.com/articles/s41586-023-06555-x",
    publication_title: "A foundation model for generalizable disease detection from retinal images (Nature, 2023)",
  },
  {
    id: "mock-chexnet",
    is_mock: true,
    name: "CheXNet",
    description: "121-layer DenseNet trained on ChestX-ray14 to detect pneumonia (and 13 other thoracic pathologies) from frontal chest radiographs at radiologist-level performance.",
    entity_type: "model",
    model_type: "classification",
    model_type_display: "Classification",
    input_types: "Chest X-ray",
    output_data_type: "Pathology probability",
    build_status: "ready",
    is_added: false,
    created_at: "2017-11-14",
    created_by_user: { first_name: "Pranav", username: "rajpurkar" },
    publication_url: "https://arxiv.org/abs/1711.05225",
    publication_title: "CheXNet: Radiologist-Level Pneumonia Detection on Chest X-Rays with Deep Learning (Rajpurkar et al., 2017)",
  },
  {
    id: "mock-biomedclip",
    is_mock: true,
    name: "BiomedCLIP",
    description: "Multimodal foundation model pretrained on 15 million biomedical image-caption pairs from PubMed Central. Enables zero-shot classification, image-text retrieval, and vision-language grounding across radiology, pathology, and microscopy.",
    entity_type: "model",
    model_type: "other",
    model_type_display: "Multimodal Foundation",
    input_types: "Biomedical image + text",
    output_data_type: "Joint embedding",
    icon: "fa-cubes",
    color: "#0ea5e9",
    build_status: "ready",
    is_added: false,
    created_at: "2023-03-02",
    created_by_user: { first_name: "Microsoft Research", username: "microsoft" },
    publication_url: "https://arxiv.org/abs/2303.00915",
    publication_title: "Large-Scale Domain-Specific Pretraining for Biomedical Vision-Language Processing (Zhang et al., 2023)",
  },
];

// Shared "Add to My Models" button. States:
//   1. unauthenticated       -> disabled + inline "please register" message
//   2. auth + preview/mock   -> disabled + "coming soon" note
//   3. auth + real, is_added -> green "Added" pill
//   4. auth + real, not yet  -> primary Add button (calls onAdd)
function AddToMyListButton({ isMock, isAdded, adding, onAdd }) {
  const authed = isAuthed();

  if (authed && !isMock && isAdded) {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 20px", background: "rgba(40,167,69,0.15)", border: "1px solid rgba(40,167,69,0.4)", borderRadius: "8px", color: "#28a745", fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>
        <i className="fa fa-check-circle" /> Added to Your Models
      </div>
    );
  }

  if (!authed) {
    return (
      <div>
        <button
          disabled
          title="Register to save models to your list"
          style={{ padding: "10px 28px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px", color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "14px", cursor: "not-allowed" }}
        >
          <i className="fa fa-lock" style={{ marginRight: "8px" }} />
          Add to My Models
        </button>
        <p style={{ marginTop: "10px", color: "#8a9ab5", fontFamily: "Poppins, sans-serif", fontSize: "13px", maxWidth: "520px", lineHeight: 1.5 }}>
          To keep a list of models you would like to use for your research, please{" "}
          <a href="/register" style={{ color: "#38b6ff", textDecoration: "underline" }}>register</a>.
        </p>
      </div>
    );
  }

  if (isMock) {
    return (
      <div>
        <button
          disabled
          title='Preview model — public "add" flow coming soon'
          style={{ padding: "10px 28px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: "8px", color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "14px", cursor: "not-allowed" }}
        >
          <i className="fa fa-clock-o" style={{ marginRight: "8px" }} />
          Add to My Models
        </button>
        <p style={{ marginTop: "10px", color: "#8a9ab5", fontFamily: "Poppins, sans-serif", fontSize: "13px" }}>
          Preview model — the "add to my list" flow for external references is coming soon.
        </p>
      </div>
    );
  }

  return (
    <button
      onClick={onAdd}
      disabled={adding}
      style={{ padding: "10px 28px", background: "#38b6ff", border: "none", borderRadius: "8px", color: "#0b0f1e", fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "14px", cursor: adding ? "not-allowed" : "pointer", opacity: adding ? 0.7 : 1 }}
    >
      <i className="fa fa-plus" style={{ marginRight: "8px" }} />
      {adding ? "Adding..." : "Add to My Models"}
    </button>
  );
}

const MODEL_TYPE_ICON = {
  segmentation: "fa-scissors",
  classification: "fa-tags",
  detection: "fa-search",
  registration: "fa-arrows",
  generation: "fa-magic",
  other: "fa-cube",
};

const MODEL_TYPE_COLORS = {
  segmentation: "#7c3aed",
  classification: "#2563eb",
  detection: "#0891b2",
  registration: "#059669",
  generation: "#b45309",
  other: "#374151",
};

function ModelTypeTag({ type, label }) {
  const bg = MODEL_TYPE_COLORS[type] || "#374151";
  return (
    <span style={{ background: bg, color: "#fff", borderRadius: "4px", padding: "2px 8px", fontSize: "11px", fontFamily: "Poppins, sans-serif", fontWeight: 600, marginRight: "4px" }}>
      {label}
    </span>
  );
}

function ModelList({ models, selected, onSelect, query, onQueryChange }) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* Search */}
      <div style={{ padding: "16px 16px 12px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ position: "relative" }}>
          <i className="fa fa-search" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#5a6a80", fontSize: "14px" }} />
          <input
            type="text"
            placeholder="Search models..."
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            style={{
              width: "100%", padding: "10px 12px 10px 36px", background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "#fff",
              fontFamily: "Poppins, sans-serif", fontSize: "14px", outline: "none", boxSizing: "border-box",
            }}
          />
          {query && (
            <button onClick={() => onQueryChange("")} style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#5a6a80", cursor: "pointer", padding: 0 }}>
              <i className="fa fa-times" />
            </button>
          )}
        </div>
        <p style={{ margin: "8px 0 0", fontSize: "13px", color: "#5a6a80", fontFamily: "Poppins, sans-serif" }}>
          {models.length} model{models.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* List — explicit maxHeight so it scrolls independently */}
      <div style={{ overflowY: "auto", maxHeight: "calc(100vh - 260px)" }}>
        {models.length === 0 && (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "15px" }}>
            No models match your search
          </div>
        )}
        {models.map(m => {
          const isActive = selected?.id === m.id;
          const icon = m.icon || MODEL_TYPE_ICON[m.model_type] || "fa-cube";
          const color = m.color || MODEL_TYPE_COLORS[m.model_type] || "#38b6ff";
          return (
            <button
              key={m.id}
              onClick={() => onSelect(m)}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: "14px",
                padding: "14px 16px", border: "none", borderBottom: "1px solid rgba(255,255,255,0.05)",
                background: isActive ? "rgba(56,182,255,0.12)" : "transparent",
                borderLeft: isActive ? "3px solid #38b6ff" : "3px solid transparent",
                cursor: "pointer", textAlign: "left", transition: "background 0.15s",
              }}
            >
              <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", flexShrink: 0 }}>
                <i className={`fa ${icon}`} style={{ color }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: isActive ? "#38b6ff" : "#e2e8f0", fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "15px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {m.name}
                </div>
                <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "12px", marginTop: "3px" }}>
                  {m.model_type_display || m.model_type || "Model"}
                  {m.created_by_user && ` · by ${m.created_by_user.first_name || m.created_by_user.username}`}
                </div>
              </div>
              {m.is_added && (
                <div style={{ color: "#28a745", fontFamily: "Poppins, sans-serif", fontSize: "11px", flexShrink: 0, textAlign: "right" }}>
                  <i className="fa fa-check-circle" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ModelDetailPanel({ model, onAdd, adding }) {
  if (!model) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#5a6a80", fontFamily: "Poppins, sans-serif" }}>
      Select a model to view details
    </div>
  );

  const icon = model.icon || MODEL_TYPE_ICON[model.model_type] || "fa-cube";
  const color = model.color || MODEL_TYPE_COLORS[model.model_type] || "#38b6ff";

  return (
    <div style={{ padding: "32px 36px", overflowY: "auto", height: "100%" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "28px" }}>
        <div style={{ width: "72px", height: "72px", borderRadius: "50%", background: "rgba(255,255,255,0.08)", border: "2px solid rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "30px", flexShrink: 0 }}>
          <i className={`fa ${icon}`} style={{ color }} />
        </div>
        <div>
          <h2 style={{ margin: 0, color: "#ffffff", fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "22px" }}>{model.name}</h2>
          <p style={{ margin: "4px 0 0", color: "#8a9ab5", fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>
            by {model.created_by_user?.first_name || model.created_by_user?.username || "Unknown"}
          </p>
          <p style={{ margin: "2px 0 0", color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "13px" }}>Community Shared Model</p>
        </div>
      </div>

      <div style={{ borderBottom: "1px solid rgba(255,255,255,0.08)", marginBottom: "24px" }} />

      <h4 style={{ color: "#e2e8f0", fontFamily: "Poppins, sans-serif", fontWeight: 600, fontSize: "15px", marginBottom: "20px" }}>Model Information</h4>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px 24px", marginBottom: "24px" }}>
        <div>
          <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Type</div>
          <div>
            <ModelTypeTag type={model.model_type} label={model.model_type_display || model.model_type || "Model"} />
          </div>
        </div>
        <div>
          <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Build Status</div>
          <div style={{ color: "#e2e8f0", fontFamily: "Poppins, sans-serif", fontSize: "14px", textTransform: "capitalize" }}>
            {model.build_status || "unknown"}
          </div>
        </div>
        {model.input_types && (
          <div>
            <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Input</div>
            <div style={{ color: "#e2e8f0", fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>{model.input_types}</div>
          </div>
        )}
        {model.output_data_type && (
          <div>
            <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Output</div>
            <div style={{ color: "#e2e8f0", fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>{model.output_data_type}</div>
          </div>
        )}
        {model.created_at && (
          <div>
            <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Created</div>
            <div style={{ color: "#e2e8f0", fontFamily: "Poppins, sans-serif", fontSize: "14px" }}>{new Date(model.created_at).toLocaleDateString()}</div>
          </div>
        )}
      </div>

      {model.description && (
        <div style={{ marginBottom: "24px" }}>
          <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Description</div>
          <p style={{ color: "#e2e8f0", fontFamily: "Poppins, sans-serif", fontSize: "14px", lineHeight: 1.6, margin: 0 }}>{model.description}</p>
        </div>
      )}

      {model.publication_url && (
        <div style={{ marginBottom: "28px" }}>
          <div style={{ color: "#5a6a80", fontFamily: "Poppins, sans-serif", fontSize: "11px", textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>Publication</div>
          <a
            href={model.publication_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#38b6ff", fontFamily: "Poppins, sans-serif", fontSize: "14px", lineHeight: 1.5, display: "inline-flex", alignItems: "flex-start", gap: "8px" }}
          >
            <i className="fa fa-book" style={{ marginTop: "3px", flexShrink: 0 }} />
            <span>{model.publication_title || model.publication_url}</span>
          </a>
        </div>
      )}

      <AddToMyListButton isMock={!!model.is_mock} isAdded={!!model.is_added} adding={adding} onAdd={onAdd} />
    </div>
  );
}

export default function OpenMedicalModels() {
  const [models, setModels] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    getPublicModels()
      .then(data => {
        // Merge preview models with real API results. Mocks come first so a
        // first-time visitor always lands on a well-populated example.
        const merged = [...MOCK_MODELS, ...(Array.isArray(data) ? data : [])];
        setModels(merged);
        if (merged.length) setSelected(merged[0]);
      })
      .catch(() => {
        // Anonymous or API-down: still show mock models so the page isn't empty.
        setModels(MOCK_MODELS);
        setSelected(MOCK_MODELS[0]);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = query.trim()
    ? models.filter(m => [
        m.name,
        m.description || "",
        m.model_type_display || "",
        m.model_type || "",
        m.created_by_user?.first_name || "",
        m.created_by_user?.username || ""
      ].join(" ").toLowerCase().includes(query.toLowerCase()))
    : models;

  const handleAdd = async () => {
    if (!selected || selected.is_mock) return;
    setAdding(true);
    try {
      const result = await addModelToMyModels(selected.id);
      toast.success(result.message || `"${selected.name}" added to your models!`);
      const data = await getPublicModels();
      const merged = [...MOCK_MODELS, ...(Array.isArray(data) ? data : [])];
      setModels(merged);
      setSelected(merged.find(m => m.id === selected.id) || selected);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to add model");
    } finally {
      setAdding(false);
    }
  };

  const panelStyle = {
    background: "rgba(15,17,32,0.85)",
    borderRadius: "14px",
    border: "1px solid rgba(255,255,255,0.08)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <div className="dashboard-page" style={{ minHeight: "100vh", color: "#fff", display: "flex", flexDirection: "column" }}>
      <div className="starfield-background" />
      <Navbar />

      <main style={{ flex: 1, display: "flex", flexDirection: "column", padding: "20px 28px 28px", position: "relative", zIndex: 1, minHeight: 0 }}>

        {/* Title */}
        <h2 style={{ fontFamily: "Poppins, sans-serif", fontWeight: 700, fontSize: "18px", color: "#e2e8f0", marginBottom: "16px" }}>
          Open Medical Models Hub
        </h2>

        {/* Two-column layout */}
        <div style={{ display: "flex", gap: "16px", flex: 1, minHeight: 0, height: "calc(100vh - 200px)" }}>

          {/* Left — list */}
          <div style={{ ...panelStyle, flex: "0 0 420px" }}>
            {loading
              ? <div style={{ padding: "40px", textAlign: "center", color: "#5a6a80", fontFamily: "Poppins, sans-serif" }}>Loading...</div>
              : <ModelList models={filtered} selected={selected} onSelect={setSelected} query={query} onQueryChange={setQuery} />
            }
          </div>

          {/* Right — detail */}
          <div style={{ ...panelStyle, flex: 1 }}>
            <ModelDetailPanel model={selected} onAdd={handleAdd} adding={adding} />
          </div>
        </div>
      </main>
    </div>
  );
}
